import { useCallback, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { FullscreenModal } from '@/components/global/FullscreenModal/FullscreenModal'
import type { EnhanceController } from '@/hooks/caseImages'
import zLayers from 'utils/styling/zLayers'
import { EnhancePanel } from './EnhancePanel'
import * as S from './EnhancePanel.styled'
import { CASE_IMAGES_TEST_IDS } from './caseImagesTestIds'

type EnhanceModalProps = {
  enhance: EnhanceController
}

type Stage = EnhanceController['state']['stage']

/** The compare slider needs the wide dialog; the error dialog is compact. */
const sizeForStage = (stage: Stage): S.EnhanceModalSize => {
  if (stage === 'error' || stage === 'changed') return 'compact'
  if (stage === 'complete' || stage === 'compare') return 'wide'
  return 'regular'
}

/**
 * The editor's container for the Enhance flow: every stage of `EnhancePanel`
 * drawn inside the shared fullscreen modal, centred over the editor. It is
 * mounted only while a stage is active and closes (cancelling the stage,
 * which stops a running generation and bills nothing) on Cancel, the close
 * button, a click outside, or Escape. Accept closes it on its own by
 * returning the flow to idle.
 *
 * It renders through a portal on `document.body` so the editor's sidebar,
 * which is only ~270px wide and has its own stacking context, cannot clip
 * or cover it.
 */
export const EnhanceModal = ({ enhance }: EnhanceModalProps) => {
  const { t } = useTranslation()
  const { state } = enhance

  // Closing while the billable accept, or a revert, is in flight would drop
  // the dialog and then bring it back when the request settles; ignore it
  // until then.
  const canClose = !(
    (state.stage === 'complete' && state.accepting) ||
    (state.stage === 'revert' && state.saving)
  )
  const close = () => {
    if (canClose) enhance.cancel()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') return
    // The editor also listens for Escape (deselecting items); this one is
    // for the dialog only.
    event.stopPropagation()
    close()
  }

  // Focus the dialog when it mounts so Escape works right away and screen
  // readers announce it. A stable callback ref runs once, on mount, with no
  // effect needed.
  const focusOnMount = useCallback((node: HTMLDivElement | null) => {
    node?.focus()
  }, [])

  if (state.stage === 'idle') return null

  return createPortal(
    <FullscreenModal
      show={[true, close]}
      hideClose={false}
      $zIndex={zLayers.modal}
    >
      <S.ModalBody
        ref={focusOnMount}
        role="dialog"
        aria-modal="true"
        aria-label={t('caseImages.enhance.disclosureTitle')}
        tabIndex={-1}
        $size={sizeForStage(state.stage)}
        data-testid={CASE_IMAGES_TEST_IDS.ENHANCE_MODAL}
        onKeyDown={onKeyDown}
      >
        <EnhancePanel enhance={enhance} layout="modal" />
      </S.ModalBody>
    </FullscreenModal>,
    document.body,
  )
}
